import React from "react";
import Axios from "axios";
import cookies from "js-cookie";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { setCurrentLocale } from "../../../store/Actions/authActions";
import Config from "../../Config";

/**
 * Les codes du cookie `i18next` ne sont pas ceux de l'API : `du`/`fn` côté
 * front, `nl`/`fr` côté serveur. La correspondance vit ici, en un seul endroit.
 */
export const LANGUAGES = [
  { code: "du", api: "nl", short: "NL", label: "Nederlands" },
  { code: "fn", api: "fr", short: "FR", label: "Français" },
  { code: "en", api: "en", short: "EN", label: "English" },
];

/**
 * Sélecteur de langue de l'espace CP manager.
 *
 * Le rôle étant purement administratif, le manager ne passe jamais par la page
 * de profil client — qui portait jusqu'ici le seul sélecteur de l'application.
 * Sans celui-ci, il reste enfermé dans la langue de son compte.
 *
 * À la différence de l'app client, on ne recharge pas la page : `changeLanguage`
 * suffit à re-rendre les composants abonnés, et tout l'espace manager l'est.
 * Le cookie est écrit d'abord, parce que `shared/format.js` le relit à chaque
 * rendu pour les dates et les montants.
 */
const LanguagePicker = ({ authUser, dispatch }) => {
  const { t, i18n } = useTranslation();
  const active = i18n.language;

  /**
   * Persiste le choix sur le compte, comme le fait l'app client, pour que la
   * langue suive le manager d'un appareil à l'autre. L'échec est silencieux :
   * l'interface a déjà changé de langue, et un incident réseau ne doit pas
   * défaire un geste que l'utilisateur voit comme abouti.
   */
  const persist = async (apiCode) => {
    try {
      await Axios({
        url: `${Config.authURL}/auth`,
        method: "PUT",
        headers: {
          uid: `${authUser?.user_uid}`,
          client: `${authUser?.user_client}`,
          "access-token": `${authUser?.user_access_token}`,
        },
        data: { locale: apiCode },
      });
    } catch (err) {
      /* le choix reste appliqué localement */
    }
  };

  const choose = (language) => {
    if (language.code === active) return;
    cookies.set("i18next", language.code);
    i18n.changeLanguage(language.code);
    dispatch(setCurrentLocale({ ...authUser, user_locale: language.code }));
    persist(language.api);
  };

  return (
    <div className="mgr-lang" role="group" aria-label={t("Taal")}>
      {LANGUAGES.map((language) => (
        <button
          key={language.code}
          type="button"
          className={`mgr-lang-option${language.code === active ? " is-active" : ""}`}
          onClick={() => choose(language)}
          aria-pressed={language.code === active}
          title={language.label}
        >
          {language.short}
        </button>
      ))}
    </div>
  );
};

const mapStateToProps = (state) => ({
  authUser: state?.auth?.user,
});

export default connect(mapStateToProps)(LanguagePicker);
